// StatsSection.tsx
import Image from "next/image";
import { ContainerGlob } from "../../../componentsGLob/ContainerGlob";
import { AnimatedCounter } from "./AnimatedCounter";
import { BackgroundWaves } from "./BackgroundWaves";
import { HexagonHover } from "./hexagonHover";
import { motion, type Variants } from "framer-motion";
import { useTranslation } from "react-i18next";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 100, damping: 10 },
  },
};

export const StatsSection: React.FC = () => {
  const { t } = useTranslation("stats");

  const stats = [
    { id: 1, value: 51, labelKey: "stats.newJobs" },
    { id: 2, value: 20, labelKey: "stats.southworkers" },
    { id: 3, value: 14, labelKey: "stats.partnerEntities" },
    { id: 4, value: 82, labelKey: "stats.coworkers" },
  ];

  return (
    <section
      className="relative bg-gradient-to-br from-amber-300 via-orange-50 to-yellow-300 py-28 sm:py-32 lg:py-40 block leading-none"
      aria-labelledby="stats-heading"
    >
      <div className="">
        <BackgroundWaves />
      </div>

      <ContainerGlob className="relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20 lg:mb-24"
        >
          <h2
            id="stats-heading"
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6"
          >
            {t("sectionTitle")}
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-4">
            {t("sectionSubtitle")}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto rounded-full" />
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 lg:gap-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.id}
              variants={itemVariants}
              whileHover={{
                scale: 1.05,
                transition: { type: "spring", stiffness: 400, damping: 17 },
              }}
              whileTap={{ scale: 0.98 }}
              className="relative group"
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-10 hover:shadow-xl transition-all duration-300 border-0 flex flex-col justify-center group-hover:bg-white/95 h-[200px] sm:h-[220px] lg:h-[240px]">
                <HexagonHover />
                <div className="text-center">
                  <div className="mb-3 sm:mb-4 lg:mb-6">
                    <AnimatedCounter
                      value={stat.value}
                      suffix="+"
                      duration={2}
                      delay={index * 0.2}
                    />
                  </div>
                  <p className="text-base sm:text-lg lg:text-xl font-medium text-gray-600 leading-relaxed h-[48px] overflow-hidden flex items-center justify-center">
                    {t(stat.labelKey)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </ContainerGlob>

      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-20 lg:h-24 overflow-hidden">
        <Image
          src="/shape-bottom.webp"
          alt="shape"
          width={1200}
          height={80}
          className="absolute bottom-[-2px] left-0 w-full h-full object-cover"
          priority={false}
        />
      </div>
    </section>
  );
};

export default StatsSection;
